import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Apis } from '../../core/apis';
import { environment } from '../../environments/environment';
import { GenericListResponse } from '../../models/generic-list-response';
import { CountryResponse } from '../../models/reqres/country.interface';
import { BehaviorSubject, map, of, tap } from 'rxjs';

const API_COUNTRY_URL = Apis.countryList;
const API_COUNTRY_BY_ID_URL = Apis.countryListById;
const API_OTP_SENDER_IMPLEMENTATION_URL = Apis.getOTPSenderImplementation;
const API_UPDATE_COUNTRY_SERVICE_URL = Apis.updateCountryService;
const API_ADD_OTP_SENDER_URL = Apis.addOtpSender;
const API_ADD_ADDRESS_SCHEMA_URL = Apis.addCountryAddressSchema;
const API_ADD_BUSINESS_IDENTIFIER_SCHEMA_URL = Apis.addBusinessIdentifierSchema;
const API_ADD_INDIVIDUAL_IDENTIFIER_SCHEMA_URL =
  Apis.addIndividualIdentifierSchema;
const API_ADD_ADMIN_FIELD_SCHEMA_URL = Apis.addAdminFieldSchema;
const API_ADD_PARTNER_FIELD_SCHEMA_URL = Apis.addPartnerFieldSchema;
const API_ADD_CUSTOMER_FIELD_SCHEMA_URL = Apis.addCustomerFieldSchema;
const API_ADD_PAYMENT_DETAILS_FIELD_SCHEMA_URL =
  Apis.addPaymentDetailsFieldSchema;
const API_UPDATE_ADDRESS_SCHEMA_URL = Apis.updateCountryAddressSchema;
const API_UPDATE_BUSINESS_IDENTIFIER_SCHEMA_URL =
  Apis.updateBusinessIdentifierSchema;
const API_UPDATE_INDIVIDUAL_IDENTIFIER_SCHEMA_URL =
  Apis.updateIndividualIdentifierSchema;
const API_UPDATE_ADMIN_FIELD_SCHEMA_URL = Apis.updateAdminFieldSchema;
const API_UPDATE_PARTNER_FIELD_SCHEMA_URL = Apis.updatePartnerFieldSchema;
const API_UPDATE_CUSTOMER_FIELD_SCHEMA_URL = Apis.updateCustomerFieldSchema;
const API_UPDATE_PAYMENT_DETAILS_FIELD_SCHEMA_URL =
  Apis.updatePaymentDetailsFieldSchema;
const API_DOCUMENT_CATEGORY_BY_USER_TYPE_URL =
  Apis.getDocumentCategoryByUserType;
const API_ADD_COUNTRY_AS_NON_MANDATORY_FOR_DOCUMENT_CATEGORY_URL =
  Apis.addCountryAsNonMandatoryForDocumentCategory;
const API_ADD_KYC_FOR_USER_TYPE_URL = Apis.addKycs;
const API_DISABLE_KYC_URL = Apis.disableKyc;
const API_KYC_BY_ID_URL = Apis.getKycById;
const API_UPDATE_KYC_BY_ID_URL = Apis.updateKycById;
const API_FOR_SANCTION_CHECK_TYPE_URL = Apis.sanctionCheckType;
const API_TIME_UNIT_URL = Apis.timeUnit;

const API_FIELD_TYPE_URL = Apis.getFieldType;
const API_FIELD_SCHEMA_FIELD_SELECT_OPTION_TYPE_URL =
  Apis.getFieldSchemaFieldSelectOptionType;
const API_COMPLEX_FIELD_ORIENTATION_URL = Apis.getComplexFieldOrientation;
const API_SELECT_OPTION_FROM_DB_URL = Apis.getSelectOptionFromDB;
const API_FIELD_SCHEMA_VALUE_DETAILS_FOR_SELECT_OPTION_URL =
  Apis.getFieldSchemaValueDetailsForSelectOption;
const API_FOR_MODE_OF_PAYMENT_OR_PAYOUT_URL = Apis.modeOfPaymentOrPayout;

const API_Limit = environment.dataLimit;
@Injectable({
  providedIn: 'root',
})
export class CountryService implements OnDestroy {
  constructor(private http: HttpClient) {}

  getCountry(
    offsetToken: string = '',
    searchTerm: any = null,
    getOnlyServiceableCountries: boolean = false,
    getOnlyPaymentDtlSchemaCountries: boolean = false,
    countryId: any = null,
    header: any = false
  ): Observable<GenericListResponse<any>> {
    let baseURL = `${API_COUNTRY_URL}?limit=${API_Limit}`;
    if (getOnlyServiceableCountries && !getOnlyPaymentDtlSchemaCountries) {
      baseURL = `${baseURL}&serviceable=true`;
    }
    if (getOnlyPaymentDtlSchemaCountries) {
      baseURL = `${baseURL}&countryWithPaymentDtlSchema=true`;
    } else {
      baseURL = `${baseURL}`;
    }
    if (offsetToken) {
      baseURL = `${baseURL}&offsetToken=${offsetToken}`;
    }
    if (searchTerm) {
      baseURL = `${baseURL}&queryString=${searchTerm}`;
    }
    if (countryId) {
      baseURL = `${baseURL}&countryId=${countryId}`;
    }
    if (header) {
      return this.http.get<GenericListResponse<any>>(`${baseURL}`, {
        headers: new HttpHeaders({
          'Skip-Loader': 'true',
        }),
      });
    } else {
      return this.http.get<GenericListResponse<any>>(baseURL);
    }
  }

  getCountryById(countryId: any): Observable<GenericListResponse<any>> {
    return this.http.get<GenericListResponse<any>>(
      `${API_COUNTRY_BY_ID_URL}/${countryId}`
    );
  }

  otpsenderImpls(): Observable<any> {
    return this.http.get<any>(`${API_OTP_SENDER_IMPLEMENTATION_URL}`);
  }

  addOtpSender(data: any): Observable<any> {
    return this.http.post<any>(`${API_ADD_OTP_SENDER_URL}`, data);
  }

  addAddressSchema(data: any): Observable<any> {
    return this.http.post<any>(`${API_ADD_ADDRESS_SCHEMA_URL}`, data);
  }

  addBusinessIdentifierSchema(data: any): Observable<any> {
    return this.http.post<any>(
      `${API_ADD_BUSINESS_IDENTIFIER_SCHEMA_URL}`,
      data
    );
  }

  addIndividualIdentifierSchema(data: any): Observable<any> {
    return this.http.post<any>(
      `${API_ADD_INDIVIDUAL_IDENTIFIER_SCHEMA_URL}`,
      data
    );
  }

  addAdminFieldSchema(data: any): Observable<any> {
    return this.http.post<any>(`${API_ADD_ADMIN_FIELD_SCHEMA_URL}`, data);
  }

  addPartnerFieldSchema(data: any): Observable<any> {
    return this.http.post<any>(`${API_ADD_PARTNER_FIELD_SCHEMA_URL}`, data);
  }

  addCustomerFieldSchema(data: any): Observable<any> {
    return this.http.post<any>(`${API_ADD_CUSTOMER_FIELD_SCHEMA_URL}`, data);
  }

  addPaymentDetailsFieldSchema(data: any): Observable<any> {
    return this.http.post<any>(
      `${API_ADD_PAYMENT_DETAILS_FIELD_SCHEMA_URL}`,
      data
    );
  }

  updateAddressSchema(data: any): Observable<any> {
    return this.http.put<any>(`${API_UPDATE_ADDRESS_SCHEMA_URL}`, data);
  }

  updateBusinessIdentifierSchema(data: any): Observable<any> {
    return this.http.put<any>(
      `${API_UPDATE_BUSINESS_IDENTIFIER_SCHEMA_URL}`,
      data
    );
  }

  updateIndividualIdentifierSchema(data: any): Observable<any> {
    return this.http.put<any>(
      `${API_UPDATE_INDIVIDUAL_IDENTIFIER_SCHEMA_URL}`,
      data
    );
  }

  updateAdminFieldSchema(data: any): Observable<any> {
    return this.http.put<any>(`${API_UPDATE_ADMIN_FIELD_SCHEMA_URL}`, data);
  }

  updatePartnerFieldSchema(data: any): Observable<any> {
    return this.http.put<any>(`${API_UPDATE_PARTNER_FIELD_SCHEMA_URL}`, data);
  }

  updateCustomerFieldSchema(data: any): Observable<any> {
    return this.http.put<any>(`${API_UPDATE_CUSTOMER_FIELD_SCHEMA_URL}`, data);
  }

  updatePaymentDetailsFieldSchema(data: any): Observable<any> {
    return this.http.put<any>(
      `${API_UPDATE_PAYMENT_DETAILS_FIELD_SCHEMA_URL}`,
      data
    );
  }

  updateCountryService(serviceable: any, countryId: any): Observable<any> {
    return this.http.put<any>(`${API_UPDATE_COUNTRY_SERVICE_URL}`, {
      countryId: countryId,
      serviceable: !serviceable,
    });
  }

  getDocumentCategoryByUserType(userType: any): Observable<any> {
    return this.http.get<any>(
      `${API_DOCUMENT_CATEGORY_BY_USER_TYPE_URL}/${userType}`
    );
  }

  addCountryAsNonMandatoryForDocumentCategory(
    userType: any,
    documentCategoryId: any,
    countryAlpha2: any
  ): Observable<any> {
    return this.http.post<any>(
      `${API_ADD_COUNTRY_AS_NON_MANDATORY_FOR_DOCUMENT_CATEGORY_URL}/${userType}/${documentCategoryId}`,
      { countryListToAddToNonMandatoryList: [countryAlpha2] }
    );
  }

  addKycsForUserType(data: any): Observable<any> {
    return this.http.post<any>(`${API_ADD_KYC_FOR_USER_TYPE_URL}`, data);
  }

  disableKyc(kycId: any): Observable<any> {
    return this.http.put<any>(`${API_DISABLE_KYC_URL}/${kycId}`, {});
  }

  getKycById(kycId: any): Observable<any> {
    return this.http.get<any>(`${API_KYC_BY_ID_URL}/${kycId}`);
  }

  updateKycsById(data: any, id: any): Observable<any> {
    return this.http.put<any>(`${API_UPDATE_KYC_BY_ID_URL}/${id}`, data);
  }

  getSanctionCheckType(): Observable<any> {
    return this.http.get<any>(`${API_FOR_SANCTION_CHECK_TYPE_URL}`);
  }

  getTimeUnit(): Observable<any> {
    return this.http.get<any>(`${API_TIME_UNIT_URL}`);
  }

  getFieldType(): Observable<any> {
    return this.http.get<any>(`${API_FIELD_TYPE_URL}`);
  }

  getFieldSelectOptionType(): Observable<any> {
    return this.http.get<any>(
      `${API_FIELD_SCHEMA_FIELD_SELECT_OPTION_TYPE_URL}`
    );
  }

  getComplexFieldOrientation(): Observable<any> {
    return this.http.get<any>(`${API_COMPLEX_FIELD_ORIENTATION_URL}`);
  }

  getSelectOptionFromDB(fieldSchemaId: any): Observable<any> {
    return this.http.get<any>(
      `${API_SELECT_OPTION_FROM_DB_URL}/${fieldSchemaId}`
    );
  }

  fieldSchemaValueDetailsForSelectCache: { url: string; data: any[] }[] = [];
  fieldSchemaValueDetailsForSelectListSubject = new BehaviorSubject<any[]>([]);

  getFieldSchemaValueDetailsForSelectOption(
    fieldSchemaValueId: any,
    header: boolean = false
  ): Observable<any> {
    if (header) {
      let baseURL = `${API_FIELD_SCHEMA_VALUE_DETAILS_FOR_SELECT_OPTION_URL}/${fieldSchemaValueId}`;

      const cachedEntry = this.fieldSchemaValueDetailsForSelectCache.find(
        (entry) => this.areUrlsEqual(entry.url, baseURL)
      );

      if (cachedEntry) {
        this.fieldSchemaValueDetailsForSelectListSubject.next(cachedEntry.data);
        return of(cachedEntry.data);
      }
      return this.http
        .get<any>(baseURL, {
          headers: new HttpHeaders({
            'Skip-Loader': 'true',
          }),
        })
        .pipe(
          tap((res) => {
            const data = res || '';
            this.fieldSchemaValueDetailsForSelectCache.push({
              url: baseURL,
              data,
            });
            this.fieldSchemaValueDetailsForSelectListSubject.next(data);
          }),
          map((res) => res || '')
        );
    } else {
      return this.http.get<any>(
        `${API_FIELD_SCHEMA_VALUE_DETAILS_FOR_SELECT_OPTION_URL}/${fieldSchemaValueId}`
      );
    }
  }

  getModeOfPaymentOrPayoutList(): Observable<any> {
    return this.http.get<any>(`${API_FOR_MODE_OF_PAYMENT_OR_PAYOUT_URL}`);
  }

  searchCountries(
    query: string,
    limit: number = 20
  ): Observable<CountryResponse> {
    return this.http.get<CountryResponse>(
      `https://dev.service.remitall.co.uk/countries/getAll?limit=${limit}&queryString=${query}`
    );
  }

  areUrlsEqual(currentUrl: string, storedUrl: string): boolean {
    try {
      const u1 = new URL(currentUrl);
      const u2 = new URL(storedUrl);

      // Compare base parts
      if (u1.origin + u1.pathname !== u2.origin + u2.pathname) {
        return false;
      }

      // Compare search params regardless of order
      const params1 = new URLSearchParams(u1.search);
      const params2 = new URLSearchParams(u2.search);

      return params1.toString() === params2.toString();
    } catch {
      return false;
    }
  }

  ngOnDestroy() {}
}
